import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { uploadLibraryFile } from '@/hooks/useUpload';
import { getUserMediaFolder } from '@/lib/seaweedfs';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createCoreMedia,
  deleteCoreMedia,
  guessMediaType,
  listCoreMedia,
  MEDIA_TYPES,
  mediaTypeMeta,
  updateCoreMedia,
  VISIBILITIES,
  visibilityMeta,
  type CoreMedia,
} from '@/lib/coreSocle';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState {
  id: number | null;
  title: string;
  filepath: string;
  mediaType: string;
  visibility: string;
  category: string;
}

const EMPTY: FormState = { id: null, title: '', filepath: '', mediaType: 'image', visibility: 'private', category: '' };

function formatSize(bytes: number | null | undefined): string {
  const b = Number(bytes ?? 0) || 0;
  if (b <= 0) return '—';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export default function MediaTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const [media, setMedia] = useState<CoreMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ownerName = user?.name || user?.user_name || 'Marchand';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setMedia(await listCoreMedia(commerceId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (m: CoreMedia) => {
    setForm({ id: m.id, title: m.title, filepath: m.filepath, mediaType: m.media_type || 'image', visibility: m.visibility || 'private', category: m.categorie || '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.filepath.trim()) return;
    setSaving(true);
    try {
      if (form.id) {
        await updateCoreMedia(form.id, { title: form.title.trim(), mediaType: form.mediaType, visibility: form.visibility, category: form.category.trim() || null });
      } else {
        await createCoreMedia({
          ownerId: commerceId,
          ownerName,
          title: form.title.trim(),
          filepath: form.filepath.trim(),
          mediaType: form.mediaType,
          visibility: form.visibility,
          category: form.category.trim() || null,
          tenantId: org.tenant_id,
          organizationId: org.id,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m: CoreMedia) => {
    setBusyId(m.id);
    try {
      await deleteCoreMedia(m.id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  // Glisser-déposer : téléverse chaque fichier puis l'enregistre dans le socle Média.
  const handleFiles = async (files: FileList | File[]) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    setUploadMsg(null);
    const folder = getUserMediaFolder(commerceId);
    let ok = 0;
    let failed = 0;
    for (const file of list) {
      try {
        const url = await uploadLibraryFile(file, folder);
        await createCoreMedia({
          ownerId: commerceId,
          ownerName,
          title: file.name.replace(/\.[^.]+$/, ''),
          filepath: url,
          mediaType: guessMediaType(file.name, file.type),
          mimeType: file.type || null,
          size: file.size,
          storagePath: `${folder}/${file.name}`,
          visibility: 'private',
          tenantId: org.tenant_id,
          organizationId: org.id,
        });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setUploading(false);
    if (ok > 0) {
      setUploadMsg({ type: 'ok', text: `${ok} média${ok > 1 ? 's' : ''} ajouté${ok > 1 ? 's' : ''} à la médiathèque${failed ? ` · ${failed} échec(s)` : ''}.` });
      load();
    } else {
      setUploadMsg({ type: 'err', text: 'Aucun fichier n’a pu être téléversé.' });
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const visible = typeFilter === 'all' ? media : media.filter((m) => (m.media_type || 'image') === typeFilter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          <button onClick={() => setTypeFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${typeFilter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Tous</button>
          {MEDIA_TYPES.map((t) => (
            <button key={t.key} onClick={() => setTypeFilter(t.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${typeFilter === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{t.label}</button>
          ))}
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
          <i className="ri-link"></i> Référencer par URL
        </button>
      </div>

      {/* Zone de glisser-déposer */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`mb-4 rounded-lg border-2 border-dashed p-6 md:p-8 text-center cursor-pointer transition-colors ${dragging ? 'border-primary-400 bg-primary-50/60' : 'border-background-300/70 bg-background-50 hover:border-primary-300 hover:bg-background-100/60'}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }}
        />
        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 transition-colors ${dragging ? 'bg-primary-100' : 'bg-background-100'}`}>
          {uploading ? <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i> : <i className={`ri-upload-cloud-2-line text-2xl ${dragging ? 'text-primary-600' : 'text-foreground-400'}`}></i>}
        </div>
        {uploading ? (
          <p className="text-sm font-medium text-foreground-700">Téléversement en cours…</p>
        ) : (
          <>
            <p className="text-sm font-semibold text-foreground-800">Glissez-déposez vos fichiers ici</p>
            <p className="text-xs text-foreground-500 mt-1">ou cliquez pour parcourir · image, vidéo, audio, PDF, document (50 Mo max)</p>
          </>
        )}
      </div>

      {uploadMsg && (
        <div className={`mb-3 p-3 rounded-md text-xs border ${uploadMsg.type === 'ok' ? 'bg-primary-50 border-primary-200 text-primary-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
          <i className={`${uploadMsg.type === 'ok' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mr-1`}></i>{uploadMsg.text}
        </div>
      )}

      <p className="text-xs text-foreground-500 mb-4">
        Gestion centralisée des médias (image, vidéo, audio, PDF, document) — réutilisée par tous les modules. Chaque fichier déposé est envoyé dans le stockage Zifek et enregistré automatiquement dans le socle Média.
      </p>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-image-2-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun média pour ce filtre.</p>
          <p className="text-xs text-foreground-400 mt-1">Glissez un fichier dans la zone ci-dessus pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {visible.map((m) => {
            const tm = mediaTypeMeta(m.media_type);
            const vis = visibilityMeta(m.visibility);
            const isImage = (m.media_type || 'image') === 'image';
            return (
              <div key={m.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden flex flex-col">
                <div className="aspect-[4/3] bg-background-100 flex items-center justify-center overflow-hidden">
                  {isImage && m.filepath ? (
                    <img src={m.filepath} alt={m.title} className="w-full h-full object-cover" />
                  ) : (
                    <i className={`${tm.icon} text-3xl text-foreground-400`}></i>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 inline-flex items-center gap-1 whitespace-nowrap"><i className={tm.icon}></i>{tm.label}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 inline-flex items-center gap-1 whitespace-nowrap"><i className={vis.icon}></i>{vis.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground-900 truncate">{m.title}</p>
                  <p className="text-[11px] text-foreground-400">{formatSize(m.size)} · {m.categorie || 'sans catégorie'}</p>
                  <div className="flex items-center gap-1 mt-auto pt-3 border-t border-background-200/60">
                    <button onClick={() => openEdit(m)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-edit-line"></i>Modifier</button>
                    <button onClick={() => remove(m)} disabled={busyId === m.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier le média' : 'Référencer un média'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.title.trim() || !form.filepath.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre</span>
              <input className={INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, ...(form.id ? {} : { mediaType: guessMediaType(e.target.value) }) })} placeholder="Ex : Photo façade boutique" autoFocus />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">URL / chemin du fichier</span>
              <input className={INPUT} value={form.filepath} onChange={(e) => setForm({ ...form, filepath: e.target.value })} placeholder="https://… ou chemin de stockage" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type de média</span>
                <select className={INPUT} value={form.mediaType} onChange={(e) => setForm({ ...form, mediaType: e.target.value })}>
                  {MEDIA_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Catégorie</span>
                <input className={INPUT} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex : Boutique" />
              </label>
            </div>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Visibilité</span>
              <div className="flex flex-wrap gap-2">
                {VISIBILITIES.map((v) => (
                  <button key={v.key} type="button" onClick={() => setForm({ ...form, visibility: v.key })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${form.visibility === v.key ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
                    <i className={v.icon}></i>{v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}